import { alpha, darken } from '@mui/material/styles'
import { colors } from './tokens'

const hoverOpacity = 0.04 // MUI default
const selectedOpacity = 0.12 // MUI default

export const palette = {
    common: { white: '#fff' },
    primary: {
        light: alpha(colors.blue, 0.5),
        main: colors.blue,
        dark: darken(colors.blue, 0.2), // MUI default
    },
    info: {
        light: '#50cdff',
        main: `#007bc2`,
    },
    error: { main: colors.red },
    warning: {
        main: colors.yellow,
        light: alpha(colors.yellow, 0.3),
    },
    success: { main: colors.green },
    grey: {
        200: '#eee',
        300: '#e0e0e0',
        500: '#9e9e9e', // MUI default
    },
    text: {
        primary: 'rgba(0, 0, 0, 0.87)',
        secondary: 'rgba(0, 0, 0, 0.54)', // MUI default
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    background: {
        paper: '#fff',
        default: '#fafafa', // MUI default
    },
    action: {
        active: 'rgba(0, 0, 0, 0.54)',
        hoverOpacity,
        hover: alpha(colors.blue, hoverOpacity),
        selectedOpacity,
        selected: alpha(colors.blue, selectedOpacity),
    },
}

// Matches hex color values https://regex101.com/r/TNsRP8/1
const hexPattern = /^#(?<hexValue>\w{3}|\w{6})$/

// Matches RGB color values https://regex101.com/r/mZ4ZY3/1
const rgbPattern = /^rgb\((?<red>[\s\w]+),(?<green>[\s\w]+),(?<blue>[\s\w]+)\)$/

// Matches RGBA color values https://regex101.com/r/IXyyqN/1
const rgbaPattern = /^rgba\((?<red>.+),(?<green>.+),(?<blue>.+),(?<alpha>.+)\)$/

// Convert hex color values to RGB color values
// Adapted from https://css-tricks.com/converting-color-spaces-in-javascript/
function hexToRGB(h: string) {
    let red: string | number = 0,
        green: string | number = 0,
        blue: string | number = 0

    // 3 digits
    if (h.length == 4) {
        red = '0x' + h[1] + h[1]
        green = '0x' + h[2] + h[2]
        blue = '0x' + h[3] + h[3]

        // 6 digits
    } else if (h.length == 7) {
        red = '0x' + h[1] + h[2]
        green = '0x' + h[3] + h[4]
        blue = '0x' + h[5] + h[6]
    }

    return `rgb(${Number(red)}, ${Number(green)}, ${Number(blue)})`
}

// Get the RGB value for a given RGBA color when its placed over a particular background
// For example this can make a translucent color appear to be opaque (by putting white behind it)
//     so it can be used as a background without letting content bleed through from underneath
// Adapted from https://marcodiiga.github.io/rgba2rgb/rgba2rgb.html
export function rgba2rgb(foregroundRgba: string, backgroundRgbOrHex = '#ffffff'): string | undefined {
    if (hexPattern.test(backgroundRgbOrHex)) {
        backgroundRgbOrHex = hexToRGB(backgroundRgbOrHex)
    }

    if (!rgbaPattern.test(foregroundRgba) || !rgbPattern.test(backgroundRgbOrHex)) {
        return undefined
    }

    // Separate the R,G,B, and A values
    const foregroundRgb = rgbaPattern.exec(foregroundRgba)?.groups
    const backgroundRgb = rgbPattern.exec(backgroundRgbOrHex)?.groups

    if (!foregroundRgb || !foregroundRgb.alpha || !backgroundRgb) {
        return undefined
    }

    // Combine colors to make RGB
    const alpha = parseFloat(foregroundRgb.alpha)
    const rgbValues = ['red', 'green', 'blue'].map((color) => {
        const foregroundColor = foregroundRgb[color]

        if (!foregroundColor) {
            throw new Error(`Color “${color}” not found in foregroundRgb “${foregroundRgb}”`)
        }

        const backgroundColor = backgroundRgb[color]

        if (!backgroundColor) {
            throw new Error(`Color “${color}” not found in backgroundRgb “${backgroundRgb}”`)
        }

        return Math.round((1 - alpha) * parseFloat(backgroundColor) + alpha * parseFloat(foregroundColor))
    })

    // Format as a CSS string
    return `rgb(${rgbValues.join(', ')})`
}

export default {}
